/**
 * Point d'entrée principal de l'application
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { PdmTheme } from './styles/primevue-theme'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import router from './router'
import App from './App.vue'

// Styles
import './styles/global.css'

// PrimeVue styles
import 'primeicons/primeicons.css'

// Création de l'application
const app = createApp(App)

// Plugins
app.use(createPinia())
app.use(router)
app.use(ToastService)
app.use(ConfirmationService)
app.use(DialogService)

// Locale française pour tous les composants PrimeVue (DatePicker en
// priorité : jours, mois, navigation). Liste exhaustive issue de la doc
// PrimeVue 4 — uniquement les clés courantes utilisées dans l'app sont
// francisées, les autres restent vides (fallback PrimeVue par défaut).
const FR_LOCALE = {
  startsWith: 'Commence par',
  contains: 'Contient',
  notContains: 'Ne contient pas',
  endsWith: 'Termine par',
  equals: 'Égal à',
  notEquals: 'Différent de',
  noFilter: 'Aucun filtre',
  lt: 'Inférieur à',
  lte: 'Inférieur ou égal à',
  gt: 'Supérieur à',
  gte: 'Supérieur ou égal à',
  dateIs: 'Date est',
  dateIsNot: 'Date n’est pas',
  dateBefore: 'Date avant',
  dateAfter: 'Date après',
  clear: 'Effacer',
  apply: 'Appliquer',
  matchAll: 'Tous',
  matchAny: 'Au moins un',
  addRule: 'Ajouter un filtre',
  removeRule: 'Supprimer un filtre',
  accept: 'Oui',
  reject: 'Non',
  choose: 'Choisir',
  upload: 'Téléverser',
  cancel: 'Annuler',
  completed: 'Terminé',
  pending: 'En attente',
  fileSizeTypes: ['o', 'Ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo'],
  dayNames: [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ],
  dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
  monthNames: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ],
  monthNamesShort: [
    'Janv',
    'Févr',
    'Mars',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Août',
    'Sept',
    'Oct',
    'Nov',
    'Déc',
  ],
  chooseYear: 'Choisir une année',
  chooseMonth: 'Choisir un mois',
  chooseDate: 'Choisir une date',
  prevDecade: 'Décennie précédente',
  nextDecade: 'Décennie suivante',
  prevYear: 'Année précédente',
  nextYear: 'Année suivante',
  prevMonth: 'Mois précédent',
  nextMonth: 'Mois suivant',
  prevHour: 'Heure précédente',
  nextHour: 'Heure suivante',
  prevMinute: 'Minute précédente',
  nextMinute: 'Minute suivante',
  prevSecond: 'Seconde précédente',
  nextSecond: 'Seconde suivante',
  am: 'am',
  pm: 'pm',
  dateFormat: 'dd/mm/yy',
  firstDayOfWeek: 1, // semaine commence un lundi en France
  today: 'Aujourd’hui',
  weekHeader: 'Sem',
  weak: 'Faible',
  medium: 'Moyen',
  strong: 'Fort',
  passwordPrompt: 'Saisir un mot de passe',
  emptyFilterMessage: 'Aucun résultat',
  emptyMessage: 'Aucune option disponible',
  aria: {
    trueLabel: 'Vrai',
    falseLabel: 'Faux',
    nullLabel: 'Non sélectionné',
    star: '1 étoile',
    stars: '{star} étoiles',
    selectAll: 'Tout sélectionner',
    unselectAll: 'Tout désélectionner',
    close: 'Fermer',
    previous: 'Précédent',
    next: 'Suivant',
    navigation: 'Navigation',
    scrollTop: 'Faire défiler en haut',
    moveTop: 'Déplacer en haut',
    moveUp: 'Déplacer vers le haut',
    moveDown: 'Déplacer vers le bas',
    moveBottom: 'Déplacer en bas',
    moveToTarget: 'Déplacer vers la cible',
    moveToSource: 'Déplacer vers la source',
    moveAllToTarget: 'Tout déplacer vers la cible',
    moveAllToSource: 'Tout déplacer vers la source',
    pageLabel: '{page}',
    firstPageLabel: 'Première page',
    lastPageLabel: 'Dernière page',
    nextPageLabel: 'Page suivante',
    previousPageLabel: 'Page précédente',
    rowsPerPageLabel: 'Lignes par page',
    jumpToPageDropdownLabel: 'Aller à la page',
    jumpToPageInputLabel: 'Aller à la page',
    selectRow: 'Sélectionner la ligne',
    unselectRow: 'Désélectionner la ligne',
    expandRow: 'Déplier la ligne',
    collapseRow: 'Replier la ligne',
    showFilterMenu: 'Afficher le menu de filtre',
    hideFilterMenu: 'Masquer le menu de filtre',
    filterOperator: 'Opérateur de filtre',
    filterConstraint: 'Contrainte de filtre',
    editRow: 'Éditer la ligne',
    saveEdit: 'Enregistrer',
    cancelEdit: 'Annuler',
    listView: 'Vue liste',
    gridView: 'Vue grille',
    slide: 'Diapositive',
    slideNumber: '{slideNumber}',
    zoomImage: 'Zoomer',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    rotateRight: 'Rotation droite',
    rotateLeft: 'Rotation gauche',
  },
}

// Configuration PrimeVue avec thème personnalisé (or bronze Prix du Marché)
app.use(PrimeVue, {
  theme: {
    preset: PdmTheme,
    options: {
      prefix: 'p',
      darkModeSelector: '[data-theme="dark"]',
      cssLayer: false,
    },
  },
  ripple: true,
  inputStyle: 'filled',
  locale: FR_LOCALE,
})

// Masquer le loader initial une fois l'app montée
app.mount('#app')

// Masquer le loader avec un petit délai pour éviter le flash
setTimeout(() => {
  const loader = document.getElementById('app-loader')
  if (loader) {
    loader.classList.add('hidden')
    setTimeout(() => loader.remove(), 300)
  }
}, 100)
